import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { X, TrendingUp } from "lucide-react";
import { moodStyleFor } from "./MoodIndicator";

const MOOD_ORDER = ["urgent", "tense", "sad", "neutral", "happy", "playful", "romantic"];
const MOOD_Y = { urgent: 6, tense: 5, sad: 4, neutral: 3, happy: 2, playful: 1, romantic: 0 };

function fmt(iso) {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

export default function MoodTimeline({ open, conversationId, conversationName, onClose }) {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !conversationId) return;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/conversations/${conversationId}/mood-timeline`);
        setSnapshots(data.snapshots || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, conversationId]);

  const points = useMemo(() => {
    if (!snapshots.length) return [];
    const start = new Date(snapshots[0].created_at).getTime();
    const end = new Date(snapshots[snapshots.length - 1].created_at).getTime();
    const range = Math.max(end - start, 1);
    return snapshots.map((s, i) => {
      const t = new Date(s.created_at).getTime();
      const x = snapshots.length === 1 ? 50 : ((t - start) / range) * 100;
      const y = (MOOD_Y[s.mood] ?? 3) / 6;
      return { ...s, x, y };
    });
  }, [snapshots]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="mood-timeline-dialog">
      <div className="bg-white border-hard shadow-hard-lg rounded-2xl w-full max-w-2xl p-6 slide-up max-h-[85vh] overflow-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <TrendingUp strokeWidth={3} className="w-6 h-6" />
            <h2 className="font-display text-2xl">Ruh hali zaman çizelgesi</h2>
          </div>
          <button onClick={onClose} data-testid="mood-timeline-close" className="w-9 h-9 border-hard-2 rounded-lg flex items-center justify-center press-btn">
            <X strokeWidth={3} className="w-4 h-4" />
          </button>
        </div>
        <p className="text-sm text-[#555] mb-3">Son 24 saatte <b>{conversationName}</b> sohbetinin ruh hali</p>

        {loading && <div className="text-center py-8 font-semibold">Yükleniyor…</div>}

        {!loading && snapshots.length === 0 && (
          <div className="text-center py-8 text-[#555] font-semibold">
            Henüz ruh hali kaydı yok — sohbete devam edin, birazdan burada görünecek.
          </div>
        )}

        {!loading && snapshots.length > 0 && (
          <>
            <div className="border-hard-2 rounded-xl p-4 bg-[#F9F8F6]" data-testid="mood-chart">
              <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="w-full h-40">
                {/* Grid */}
                {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                  <line key={i} x1="0" x2="100" y1={(i / 6) * 40} y2={(i / 6) * 40} stroke="#0A0A0A11" strokeWidth="0.15" />
                ))}
                {/* Path */}
                {points.length > 1 && (
                  <motion.path
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1.2, ease: "easeOut" }}
                    d={"M " + points.map((p) => `${p.x} ${p.y * 40}`).join(" L ")}
                    fill="none"
                    stroke="#0A0A0A"
                    strokeWidth="0.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                )}
                {/* Points */}
                {points.map((p, i) => {
                  const s = moodStyleFor(p.mood);
                  return (
                    <g key={i}>
                      <motion.circle
                        cx={p.x}
                        cy={p.y * 40}
                        r={1.4}
                        fill={s.accent}
                        stroke="#0A0A0A"
                        strokeWidth="0.3"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: i * 0.05, type: "spring", stiffness: 400 }}
                      />
                    </g>
                  );
                })}
              </svg>
              <div className="flex justify-between text-[10px] font-black uppercase tracking-widest text-[#555] mt-1">
                <span>{fmt(snapshots[0].created_at)}</span>
                <span>Şimdi</span>
              </div>
            </div>

            {/* Legend */}
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              {MOOD_ORDER.map((m) => {
                const s = moodStyleFor(m);
                return (
                  <div key={m} className="flex items-center gap-1 text-[11px] font-bold border-hard-2 rounded-full px-2 py-0.5" style={{ color: s.accent }}>
                    <span className="w-2 h-2 rounded-full inline-block" style={{ background: s.accent }} /> {s.label}
                  </div>
                );
              })}
            </div>

            {/* List */}
            <div className="mt-4 divide-y-2 divide-[#0A0A0A11]">
              {snapshots.slice().reverse().slice(0, 10).map((s) => (
                <div key={s.id} className="flex items-center gap-3 py-2">
                  <span className="text-xl">{s.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-black" style={{ color: moodStyleFor(s.mood).accent }}>{moodStyleFor(s.mood).label}</div>
                    <div className="text-xs text-[#555] truncate">{s.reason}</div>
                  </div>
                  <div className="text-[11px] font-bold text-[#555]">{fmt(s.created_at)}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
